import { create } from 'zustand';
import { isPlaySource, useConsole } from './console.js';

import { useTerrain } from '../components/terrain/terrain-store.js';
import { getView, type LiveEntity } from '../engine/prism.js';
import { describeKeep, keepCommands, keepKey, type KeepItem } from '../lib/live-world.js';
import type { EditorCommand } from '../lib/scene-doc.js';
import { syncPlayAfterRun, useScene } from './scene.js';
import { useWorkspace } from './workspace.js';

/**
 * Play mode transport. The Play tab owns the engine view; this store holds the
 * requested state and what the engine reports back.
 */

export type PlayState = 'stopped' | 'starting' | 'playing' | 'paused';

interface PlayStore {
  state: PlayState;
  /** Bumped to (re)start a run; the Play view restarts when it changes. */
  runId: number;
  fps: number;
  drawCalls: number;
  triangles: number;
  /** Errors from the current run, for "Ask AI to fix". */
  errors: string[];
  /**
   * Play-mode terrain changes reported when the last run stopped. Discarded
   * unless the user keeps them ({@link PlayStore.keepTerrain}).
   */
  terrainChanges: { path: string; text: string; edits: number } | null;
  /** Editor play speed (engine time multiplier, 1 = normal). */
  speed: number;
  /** Game frame after the last frame step. */
  frame: number;
  /** Tint the editor while playing (Unity's play mode tint). */
  tint: PlayTint;
  /** Running entity shown in the live Inspector. */
  liveSelected: string | null;
  /** Parts of the running world to write back to the scene on stop. */
  keep: KeepItem[];
  /** Parts edited in the live Inspector this run (suggested for keeping). */
  edited: KeepItem[];
  /** Kept play-mode changes waiting for Apply / Discard after the run stopped. */
  keptChanges: { commands: EditorCommand[]; labels: string[]; skipped: string[] } | null;

  play: () => void;
  /** Paused: advance one frame. */
  step: () => void;
  setSpeed: (speed: number) => void;
  setTint: (tint: Partial<PlayTint>) => void;
  selectLive: (id: string | null) => void;
  toggleKeep: (item: KeepItem) => void;
  markEdited: (item: KeepItem) => void;
  /** Writes the kept play-mode changes into the scene (one undo step). */
  applyKept: () => void;
  discardKept: () => void;
  togglePause: () => void;
  stop: () => void;
  restart: () => void;
  setState: (state: PlayState) => void;
  stats: (fps: number, drawCalls: number, triangles: number) => void;
  error: (message: string) => void;
  terrainChanged: (path: string, text: string, edits: number) => void;
  /** "Keep play-mode terrain changes": writes them to the terrain data file. */
  keepTerrain: () => void;
  discardTerrain: () => void;
}

let startRequest = 0;

export interface PlayTint {
  enabled: boolean;
  /** CSS colour of the tint over the editor chrome while playing. */
  color: string;
}

const TINT_KEY = 'prism.playTint';
export const DEFAULT_TINT: PlayTint = { enabled: true, color: '#3a6fd8' };

function readTint(): PlayTint {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(TINT_KEY);
    const v = raw ? (JSON.parse(raw) as Partial<PlayTint>) : {};
    return { enabled: typeof v.enabled === 'boolean' ? v.enabled : DEFAULT_TINT.enabled, color: typeof v.color === 'string' ? v.color : DEFAULT_TINT.color };
  } catch {
    return DEFAULT_TINT;
  }
}

/** Snapshots of the kept entities, read from the running game just before it stops. */
async function snapshotKept(keep: readonly KeepItem[]): Promise<Map<string, LiveEntity | null>> {
  const view = getView('play');
  const ids = [...new Set(keep.map((item) => item.id))];
  return new Map(await Promise.all(ids.map(async (id) => [id, view ? await view.liveEntity(id) : null] as const)));
}

export const usePlay = create<PlayStore>((set, get) => ({
  state: 'stopped',
  runId: 0,
  fps: 0,
  drawCalls: 0,
  triangles: 0,
  errors: [],
  terrainChanges: null,
  speed: 1,
  frame: 0,
  tint: readTint(),
  liveSelected: null,
  keep: [],
  edited: [],
  keptChanges: null,

  play: async () => {
    const { state } = get();
    useWorkspace.getState().setView('play');
    if (state === 'paused') {
      get().togglePause();
      return;
    }
    if (state === 'playing' || state === 'starting') return;
    const request = ++startRequest;
    const project = useWorkspace.getState().projectId;
    // Unsaved scene edits must reach disk before the game reads them.
    await useScene.getState().flush();
    if (
      request !== startRequest ||
      get().state !== state ||
      useWorkspace.getState().projectId !== project
    )
      return;
    useConsole.getState().resolveSource(isPlaySource);
    set((s) => ({ state: 'starting', runId: s.runId + 1, errors: [], fps: 0, terrainChanges: null, keep: [], edited: [], keptChanges: null, liveSelected: null }));
  },

  togglePause: () => {
    const { state } = get();
    if (state !== 'playing' && state !== 'paused') return;
    const paused = state === 'playing';
    getView('play')?.pause(paused);
    set({ state: paused ? 'paused' : 'playing' });
  },

  stop: () => {
    startRequest++;
    const { keep, state } = get();
    const finish = (keptChanges: PlayStore['keptChanges']) => {
      getView('play')?.stop();
      syncPlayAfterRun();
      set({ state: 'stopped', fps: 0, keptChanges, keep: [], edited: [], liveSelected: null });
    };
    if (keep.length === 0 || !useScene.getState().doc || state === 'stopped') {
      finish(null);
      return;
    }
    // Read the kept entities from the running game first, then stop it.
    const run = startRequest;
    void snapshotKept(keep).then((snapshots) => {
      // A new run started meanwhile, or the scene closed.
      const doc = useScene.getState().doc;
      if (run !== startRequest || !doc) return finish(null);
      const names = new Map([...snapshots].map(([id, e]) => [id, e?.name || id]));
      const { commands, skipped } = keepCommands(doc, snapshots, keep);
      finish(
        commands.length || skipped.length
          ? { commands, labels: keep.filter((k) => !skipped.includes(k)).map((k) => describeKeep(k, names)), skipped: skipped.map((k) => describeKeep(k, names)) }
          : null,
      );
    });
  },

  step: () => {
    const { state } = get();
    if (state === 'playing') get().togglePause();
    if (get().state !== 'paused') return;
    void getView('play')?.step();
  },

  setSpeed: (speed) => {
    const clamped = Number.isFinite(speed) ? Math.min(16, Math.max(0, speed)) : 1;
    set({ speed: clamped });
    getView('play')?.setPlaySpeed(clamped);
  },

  setTint: (patch) => {
    const tint = { ...get().tint, ...patch };
    set({ tint });
    try {
      localStorage.setItem(TINT_KEY, JSON.stringify(tint));
    } catch {
      // Storage blocked: the tint lasts for this session.
    }
  },

  selectLive: (id) => set({ liveSelected: id }),

  toggleKeep: (item) =>
    set((s) => {
      const key = keepKey(item);
      const has = s.keep.some((k) => keepKey(k) === key);
      return { keep: has ? s.keep.filter((k) => keepKey(k) !== key) : [...s.keep, item] };
    }),

  markEdited: (item) =>
    set((s) => (s.edited.some((k) => keepKey(k) === keepKey(item)) ? s : { edited: [...s.edited, item] })),

  applyKept: () => {
    const kept = get().keptChanges;
    if (!kept) return;
    if (kept.commands.length) useScene.getState().applyBatch(kept.commands);
    set({ keptChanges: null });
  },

  discardKept: () => set({ keptChanges: null }),

  restart: async () => {
    const request = ++startRequest;
    const project = useWorkspace.getState().projectId;
    getView('play')?.stop();
    syncPlayAfterRun();
    await useScene.getState().flush();
    if (request !== startRequest || useWorkspace.getState().projectId !== project) return;
    useConsole.getState().resolveSource(isPlaySource);
    set((s) => ({ state: 'starting', runId: s.runId + 1, errors: [], fps: 0, terrainChanges: null, keep: [], edited: [], liveSelected: null }));
  },

  setState: (state) => set({ state }),
  stats: (fps, drawCalls, triangles) => set({ fps, drawCalls, triangles }),
  error: (message) => set((s) => ({ errors: [...s.errors, message].slice(-50) })),
  terrainChanged: (path, text, edits) => set({ terrainChanges: { path, text, edits } }),
  keepTerrain: () => {
    const c = get().terrainChanges;
    if (!c) return;
    useTerrain.getState().fromPlay(c.path, c.text);
    set({ terrainChanges: null });
  },
  discardTerrain: () => set({ terrainChanges: null }),
}));

/** Play mode tint: a class + colour on the document while a run is active. */
function applyTint(state: PlayStore): void {
  if (typeof document === 'undefined') return;
  const on = state.tint.enabled && state.state !== 'stopped';
  document.body.classList.toggle('is-play-tinted', on);
  document.documentElement.style.setProperty('--play-tint', state.tint.color);
}
usePlay.subscribe((state, prev) => {
  if (state.state !== prev.state || state.tint !== prev.tint) applyTint(state);
});
